/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {dlsColor} from '../../../theme/theme';

/**
 * The content width inside every Asset Details Drawer card - the 456px card
 * less its 16px side padding.
 */
export const ASSET_DETAILS_CONTENT_WIDTH = '424px';

/**
 * The Asset Details Drawer's current theme content styles - the label, value,
 * text and bordered tile styles its designed sections (Additional Info,
 * Diagnostics, Spreader, Tags) share, for the sections without a design of
 * their own (Smarter Trailer, Generic Sensor and the SC device sensor
 * graphs) to match them.
 *
 * - `label`: an uppercase field label (`LabelValueRow`, `DiagnosticTile`).
 * - `valueText`: a tile's prominent value (`DiagnosticTile`, `SpreaderStatTile`).
 * - `unit`: a value's unit (`LabelValueRow`).
 * - `text`: body text and inline values (`InfoTwoColumnGrid`, `ToolCard`).
 * - `meta`: small supporting text, e.g. a last report time (`ToolCard`).
 * - `title`: a card or sub-section title (`ToolCard`).
 * - `emptyText`: centered empty-state text (`TagsEmptyState`).
 * - `tile`: a white, grey-bordered, rounded tile (`InfoSection`).
 *
 * ```typescript
 *
 *   import {getAssetDetailsContentStyles} from
 *     '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
 *   ...
 *
 *   const useStyles = makeStyles(() => {
 *     const contentStyles = getAssetDetailsContentStyles();
 *     return {
 *       currentLabel : contentStyles.label,
 *       currentValue : contentStyles.valueText
 *     };
 *   });
 *
 * ```
 *
 * @returns The `label`, `valueText`, `unit`, `text`, `meta`, `title`,
 *          `emptyText` and `tile` style objects.
 */
export const getAssetDetailsContentStyles = () => ({
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase' as const,
    color         : dlsColor('grey3')
  },
  // named `valueText`, not `value` - the signals Babel transform treats any
  // `.value` read as a signal read and injects hooks into the enclosing
  // function, which for a makeStyles creator breaks the hook order ...
  valueText : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '18px',
    lineHeight : '22px',
    color      : dlsColor('grey2')
  },
  unit : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '13px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  },
  text : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  },
  meta : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  },
  title : {
    fontFamily : 'Roboto',
    fontWeight : 500,
    fontSize   : '20px',
    lineHeight : '22px',
    color      : dlsColor('grey2')
  },
  emptyText : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    textAlign     : 'center' as const,
    color         : dlsColor('grey2')
  },
  tile : {
    boxSizing       : 'border-box' as const,
    padding         : '8px',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey7')}`,
    borderRadius    : '8px'
  }
});

export default getAssetDetailsContentStyles;
